import { useEffect, useState, useMemo } from 'react'
import { Box, Flex, Text, VStack, Spinner, Code } from '@chakra-ui/react'
import { motion } from 'framer-motion'
import { useNavigate, useParams } from 'react-router-dom'
import {
  LuArrowLeft,
  LuShieldCheck,
  LuShieldAlert,
  LuPlus,
  LuPencil,
  LuTrash2,
  LuDatabase,
  LuCopy,
  LuCheck,
} from 'react-icons/lu'
import { themeColors, brand, statusColors } from '../../theme'
import { auditService } from '../../api/services/audit.service'
import type { AuditRecord, EntityAuditVerificationResponse } from '../../api/models/audit.models'

const MotionBox = motion.create(Box)

const operationConfig: Record<string, { icon: typeof LuPlus; label: string; color: string }> = {
  INSERT: { icon: LuPlus, label: 'Creado', color: statusColors.success },
  UPDATE: { icon: LuPencil, label: 'Modificado', color: statusColors.info },
  DELETE: { icon: LuTrash2, label: 'Eliminado', color: statusColors.error },
  SEED: { icon: LuDatabase, label: 'Baseline', color: '#8B5CF6' },
}

function HashCopy({ hash, label }: { hash: string; label?: string }) {
  const [copied, setCopied] = useState(false)
  const colors = themeColors

  const copy = () => {
    navigator.clipboard.writeText(hash)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <Flex alignItems="center" gap={1}>
      {label && (
        <Text fontSize="xs" color={colors.textDimmed} mr={1}>
          {label}
        </Text>
      )}
      <Code
        fontSize="xs"
        px={2}
        py={0.5}
        borderRadius="md"
        bg={colors.bgCard}
        color={colors.textMuted}
      >
        {hash.substring(0, 16)}...
      </Code>
      <Box
        as="button"
        cursor="pointer"
        onClick={copy}
        color={colors.textDimmed}
        _hover={{ color: brand.green }}
      >
        {copied ? <LuCheck size={14} /> : <LuCopy size={14} />}
      </Box>
    </Flex>
  )
}

function DiffView({ current, previous }: { current: string; previous?: string }) {
  const colors = themeColors

  const changes = useMemo(() => {
    try {
      const curr = JSON.parse(current)
      const prev = previous ? JSON.parse(previous) : null

      if (!prev) return null

      const allKeys = new Set([...Object.keys(curr), ...Object.keys(prev)])
      const diffs: { key: string; oldVal: unknown; newVal: unknown }[] = []

      for (const key of allKeys) {
        if (key === 'CurrentAuditHash' || key === 'AuditVersion') continue
        const oldVal = prev[key]
        const newVal = curr[key]
        if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
          diffs.push({ key, oldVal, newVal })
        }
      }

      return diffs.length > 0 ? diffs : null
    } catch {
      return null
    }
  }, [current, previous])

  if (!changes) return null

  return (
    <Box mt={2} bg={colors.bgCard} borderRadius="lg" p={3} fontSize="xs">
      <Text fontWeight="600" color={colors.textMuted} mb={2}>
        Cambios:
      </Text>
      <VStack gap={1} alignItems="stretch">
        {changes.map(({ key, oldVal, newVal }) => (
          <Flex key={key} gap={2} alignItems="flex-start">
            <Text fontWeight="600" color={colors.text} minW="120px">
              {key}:
            </Text>
            <Box>
              {oldVal !== undefined && (
                <Text color={statusColors.error} textDecoration="line-through">
                  {typeof oldVal === 'object' ? JSON.stringify(oldVal) : String(oldVal ?? 'null')}
                </Text>
              )}
              <Text color={statusColors.success}>
                {typeof newVal === 'object' ? JSON.stringify(newVal) : String(newVal ?? 'null')}
              </Text>
            </Box>
          </Flex>
        ))}
      </VStack>
    </Box>
  )
}

export default function HistorialEntidadPage() {
  const { entityName, entityId } = useParams<{ entityName: string; entityId: string }>()
  const navigate = useNavigate()
  const colors = themeColors

  const [records, setRecords] = useState<AuditRecord[]>([])
  const [verification, setVerification] = useState<EntityAuditVerificationResponse | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!entityName || !entityId) return

    const load = async () => {
      setLoading(true)
      try {
        const [history, verify] = await Promise.all([
          auditService.getEntityAuditHistory(entityName, entityId),
          auditService.verifyEntity(entityName, entityId),
        ])
        setRecords(history)
        setVerification(verify)
      } catch {
        // Error handled by API client
      } finally {
        setLoading(false)
      }
    }

    load()
  }, [entityName, entityId])

  return (
    <VStack gap={6} alignItems="stretch">
      <MotionBox
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Header */}
        <Flex alignItems="center" gap={3} mb={6}>
          <Box
            as="button"
            display="flex"
            alignItems="center"
            justifyContent="center"
            width="36px"
            height="36px"
            borderRadius="lg"
            cursor="pointer"
            bg={colors.bgCard}
            border="1px solid"
            borderColor={colors.border}
            _hover={{ bg: colors.bgHover }}
            onClick={() => navigate(`/app/auditoria/${entityName}`)}
          >
            <LuArrowLeft size={18} color={colors.text} />
          </Box>
          <Box flex={1}>
            <Flex alignItems="center" gap={2}>
              <Text fontSize="xl" fontWeight="700" color={colors.text}>
                Historial de Auditoria
              </Text>
              {verification &&
                (verification.isValid ? (
                  <LuShieldCheck size={22} color={statusColors.success} />
                ) : (
                  <LuShieldAlert size={22} color={statusColors.error} />
                ))}
            </Flex>
            <Text fontSize="sm" color={colors.textMuted}>
              {entityName} / {entityId?.substring(0, 8)}...
            </Text>
          </Box>
        </Flex>

        {/* Verification status */}
        {verification && (
          <Box
            bg={verification.isValid ? '#eef7f6' : '#fcefeb'}
            border="1px solid"
            borderColor={verification.isValid ? '#bfe3df' : '#FECACA'}
            borderRadius="xl"
            p={4}
            mb={6}
          >
            <Flex alignItems="center" gap={3}>
              {verification.isValid ? (
                <LuShieldCheck size={24} color={statusColors.success} />
              ) : (
                <LuShieldAlert size={24} color={statusColors.error} />
              )}
              <Box>
                <Text
                  fontSize="sm"
                  fontWeight="600"
                  color={verification.isValid ? statusColors.success : statusColors.error}
                >
                  {verification.isValid ? 'Cadena de hash intacta' : 'Alteracion detectada'}
                </Text>
                <Text fontSize="xs" color={colors.textMuted}>
                  {verification.totalRecords} versiones registradas
                  {verification.firstTamperedVersion &&
                    ` - Primera alteracion en v${verification.firstTamperedVersion}`}
                </Text>
              </Box>
            </Flex>
            {/* When a tampered version was found, show the mismatched pair for THAT version.
                Otherwise (chain intact but entity hash drifted) show entity vs last audit. */}
            {verification.firstTamperedVersion && verification.actualHash && verification.expectedHash ? (
              <Flex mt={2} gap={4} flexWrap="wrap">
                <HashCopy hash={verification.actualHash} label={`Hash guardado (v${verification.firstTamperedVersion}):`} />
                <HashCopy hash={verification.expectedHash} label="Hash recalculado:" />
              </Flex>
            ) : verification.currentEntityHash ? (
              <Flex mt={2} gap={4} flexWrap="wrap">
                <HashCopy hash={verification.currentEntityHash} label="Hash actual:" />
                {verification.lastAuditHash && (
                  <HashCopy hash={verification.lastAuditHash} label="Hash esperado:" />
                )}
              </Flex>
            ) : null}
          </Box>
        )}

        {/* Loading */}
        {loading && (
          <Flex justifyContent="center" py={12}>
            <Spinner size="lg" color={brand.green} />
          </Flex>
        )}

        {/* Timeline */}
        {!loading && records.length > 0 && (
          <Box position="relative" pl={8}>
            {/* Vertical line */}
            <Box
              position="absolute"
              left="15px"
              top="0"
              bottom="0"
              width="2px"
              bg={colors.border}
            />

            <VStack gap={0} alignItems="stretch">
              {records.map((record, index) => {
                const config = operationConfig[record.operation] || operationConfig.UPDATE
                const OpIcon = config.icon
                const prevSnapshot = index > 0 ? records[index - 1].snapshotJson : undefined

                return (
                  <MotionBox
                    key={record.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    position="relative"
                    pb={6}
                  >
                    {/* Timeline dot */}
                    <Box
                      position="absolute"
                      left="-25px"
                      top="4px"
                      width="24px"
                      height="24px"
                      borderRadius="full"
                      bg="white"
                      border="2px solid"
                      borderColor={config.color}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <OpIcon size={12} color={config.color} />
                    </Box>

                    {/* Content */}
                    <Box
                      bg={colors.bgPanel}
                      border="1px solid"
                      borderColor={colors.border}
                      borderRadius="xl"
                      p={4}
                      ml={2}
                    >
                      <Flex justifyContent="space-between" alignItems="flex-start" mb={2}>
                        <Flex alignItems="center" gap={2}>
                          <Box px={2} py={0.5} borderRadius="md" bg={`${config.color}15`}>
                            <Text fontSize="xs" fontWeight="600" color={config.color}>
                              {config.label}
                            </Text>
                          </Box>
                          <Text fontSize="xs" color={colors.textMuted}>
                            v{record.version}
                          </Text>
                        </Flex>
                        <Text fontSize="xs" color={colors.textDimmed}>
                          {new Date(record.changedAt).toLocaleString('es-PE')}
                        </Text>
                      </Flex>

                      <Text fontSize="sm" color={colors.textSecondary} mb={2}>
                        Por: <Text as="span" fontWeight="600">{record.changedBy}</Text>
                      </Text>

                      <HashCopy hash={record.hash} label="Hash:" />

                      <DiffView current={record.snapshotJson} previous={prevSnapshot} />
                    </Box>
                  </MotionBox>
                )
              })}
            </VStack>
          </Box>
        )}

        {/* No records */}
        {!loading && records.length === 0 && (
          <Flex direction="column" alignItems="center" py={12} gap={3}>
            <Text fontSize="lg" fontWeight="600" color={colors.textMuted}>
              Sin registros de auditoria
            </Text>
            <Text fontSize="sm" color={colors.textDimmed}>
              Este registro no tiene historial de cambios aun
            </Text>
          </Flex>
        )}
      </MotionBox>
    </VStack>
  )
}
